import { HorizontalDivider, Select, SwitchFormComponent } from 'commons/components';
import { useTranslation } from 'core/i18n/i18n';
import { SettingsCardLayout } from 'modules/settings/settings.style';
import { TrainerAppConfig } from '../domain';
import { TaskTypeSelectOptions } from '../domain/model/constants';
import {
    TrainingProgramCardContent,
    TrainingProgramElementLabel,
    TrainingProgramMainTitle,
    TrainingProgramSection,
    TrainingProgramSectionDescription,
    TrainingProgramSectionElements,
    TrainingProgramSectionMeta,
    TrainingProgramSectionTitle,
    TrainingProgramTaskTypeRow,
} from './training-programs-trainer-app-config.style';

interface TrainingProgramsTrainerAppConfigProps {
    config: TrainerAppConfig;
    loading: boolean;
    onConfigChange: (config: TrainerAppConfig) => void;
}

export const TrainingProgramsTrainerAppConfigComponent: React.FC<TrainingProgramsTrainerAppConfigProps> = (props) => {
    const { t } = useTranslation();

    const { config, loading, onConfigChange } = props;

    const taskTypeOptions = TaskTypeSelectOptions.map(({ value, translationKey }) => ({
        value,
        text: t(translationKey),
    }));

    const handleOnConfigChange = (field: string, enabled: boolean) => {
        onConfigChange({
            ...config,
            [field]: enabled,
        });
    };

    const allowActions = config.allowActions;

    return (
        <SettingsCardLayout rounded>
            <TrainingProgramCardContent>
                <TrainingProgramSection>
                    <TrainingProgramSectionMeta>
                        <SwitchFormComponent
                            data-testid="switch-allowActions"
                            label={
                                <TrainingProgramMainTitle>
                                    {t('settings.training-programs.trainer-app.allow-actions.title')}
                                </TrainingProgramMainTitle>
                            }
                            onChange={(enabled: boolean) => handleOnConfigChange('allowActions', enabled)}
                            loading={loading}
                            enabled={config.allowActions}
                        />
                        <TrainingProgramSectionDescription>
                            {t('settings.training-programs.trainer-app.allow-actions.description')}
                        </TrainingProgramSectionDescription>
                    </TrainingProgramSectionMeta>
                </TrainingProgramSection>

                <HorizontalDivider />

                <TrainingProgramSection>
                    <TrainingProgramSectionMeta>
                        <TrainingProgramSectionTitle>
                            {t('settings.training-programs.trainer-app.signatures.title')}
                        </TrainingProgramSectionTitle>
                        <TrainingProgramSectionDescription>
                            {t('settings.training-programs.trainer-app.allow-actions.subtitle')}
                        </TrainingProgramSectionDescription>
                    </TrainingProgramSectionMeta>
                    <TrainingProgramSectionElements>
                        <SwitchFormComponent
                            data-testid="switch-enableTrainerSignature"
                            label={
                                <TrainingProgramElementLabel>
                                    {t('settings.training-programs.trainer-app.trainer-signature.title')}
                                </TrainingProgramElementLabel>
                            }
                            onChange={(enabled: boolean) => handleOnConfigChange('enableTrainerSignature', enabled)}
                            loading={loading}
                            enabled={config.enableTrainerSignature}
                            disabled={!allowActions}
                        />
                        <SwitchFormComponent
                            data-testid="switch-enableOperatorSignature"
                            label={
                                <TrainingProgramElementLabel>
                                    {t('settings.training-programs.trainer-app.operator-signature.title')}
                                </TrainingProgramElementLabel>
                            }
                            onChange={(enabled: boolean) => handleOnConfigChange('enableOperatorSignature', enabled)}
                            loading={loading}
                            enabled={config.enableOperatorSignature}
                            disabled={!allowActions}
                        />
                    </TrainingProgramSectionElements>
                </TrainingProgramSection>

                <TrainingProgramSection>
                    <TrainingProgramSectionMeta>
                        <SwitchFormComponent
                            data-testid="switch-enableUploadEvidence"
                            label={
                                <TrainingProgramSectionTitle>
                                    {t('settings.training-programs.trainer-app.upload-evidence.section-title')}
                                </TrainingProgramSectionTitle>
                            }
                            onChange={(enabled: boolean) => handleOnConfigChange('enableUploadEvidence', enabled)}
                            loading={loading}
                            enabled={config.enableUploadEvidence}
                            disabled={!allowActions}
                        />
                        <TrainingProgramSectionDescription>
                            {t('settings.training-programs.trainer-app.upload-evidence.title')}
                        </TrainingProgramSectionDescription>
                    </TrainingProgramSectionMeta>
                </TrainingProgramSection>

                <TrainingProgramSection>
                    <TrainingProgramSectionMeta>
                        <TrainingProgramSectionTitle>
                            {t('settings.training-programs.trainer-app.task-types.title')}
                        </TrainingProgramSectionTitle>
                    </TrainingProgramSectionMeta>
                    <TrainingProgramSectionElements>
                        <TrainingProgramTaskTypeRow>
                            <TrainingProgramElementLabel>
                                {t('settings.training-programs.trainer-app.task-types-with-evidence.row-label')}
                            </TrainingProgramElementLabel>
                            <Select
                                name="taskTypesWithEvidence"
                                placeholder={t('settings.training-programs.trainer-app.task-types-with-evidence.label')}
                                multiple
                                showSelectorControl
                                hideSelectAll
                                options={taskTypeOptions}
                                value={config.taskTypesWithEvidence}
                                disabled={!allowActions}
                                onChange={(_field, value) =>
                                    onConfigChange({ ...config, taskTypesWithEvidence: (value as string[]) ?? [] })
                                }
                            />
                        </TrainingProgramTaskTypeRow>
                    </TrainingProgramSectionElements>
                </TrainingProgramSection>
            </TrainingProgramCardContent>
        </SettingsCardLayout>
    );
};
